import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { LEAD_STAGES, LEAD_PIPELINES, type LeadStage } from "@/lib/crm";
import { toast } from "sonner";
import { Mail, Phone, Home as HomeIcon, MessageCircle, Filter, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/crm")({
  head: () => ({ meta: [{ title: "CRM — Admin" }] }),
  component: CrmBoard,
});

type LeadRow = {
  id: string;
  stage: string;
  pipeline: string;
  channel: string;
  message: string | null;
  created_at: string;
  contact: { id: string; full_name: string; email: string | null; phone: string | null } | null;
  property: { id: string; title: string; slug: string } | null;
};

function CrmBoard() {
  const qc = useQueryClient();
  const [pipeline, setPipeline] = useState<string>("");
  const [dragging, setDragging] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["crm-leads"],
    queryFn: async (): Promise<LeadRow[]> => {
      const { data, error } = await supabase
        .from("leads")
        .select(
          "id, stage, pipeline, channel, message, created_at, contact:contacts(id, full_name, email, phone), property:properties(id, title, slug)",
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as LeadRow[];
    },
  });

  const filtered = useMemo(
    () => (data ?? []).filter((l) => (pipeline ? l.pipeline === pipeline : true)),
    [data, pipeline],
  );

  const byStage = useMemo(() => {
    const map: Record<string, LeadRow[]> = {};
    for (const s of LEAD_STAGES) map[s.value] = [];
    for (const l of filtered) (map[l.stage] ??= []).push(l);
    return map;
  }, [filtered]);

  const moveStage = async (leadId: string, stage: LeadStage) => {
    const prev = qc.getQueryData<LeadRow[]>(["crm-leads"]);
    qc.setQueryData<LeadRow[]>(["crm-leads"], (old) =>
      (old ?? []).map((l) => (l.id === leadId ? { ...l, stage } : l)),
    );
    const { error } = await supabase.from("leads").update({ stage }).eq("id", leadId);
    if (error) {
      qc.setQueryData(["crm-leads"], prev);
      toast.error(error.message);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">CRM · Pipeline de leads</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {filtered.length} leads · arrastrá una tarjeta para cambiar de etapa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            aria-label="Pipeline"
            value={pipeline}
            onChange={(e) => setPipeline(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Todos los pipelines</option>
            {LEAD_PIPELINES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-10 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando leads…
        </div>
      ) : (
        <div className="mt-6 -mx-6 overflow-x-auto px-6 pb-4">
          <div className="flex gap-4 min-w-max">
            {LEAD_STAGES.map((s) => (
              <div
                key={s.value}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragging) {
                    moveStage(dragging, s.value);
                    setDragging(null);
                  }
                }}
                className="flex w-72 flex-col rounded-xl border border-border bg-secondary/40 p-3"
              >
                <div className="mb-3 flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2 w-2 rounded-full ${s.tone.split(" ")[0]}`} />
                    <h3 className="text-sm font-semibold">{s.label}</h3>
                  </div>
                  <span className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {byStage[s.value]?.length ?? 0}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 min-h-[100px]">
                  {byStage[s.value]?.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      onDragStart={() => setDragging(lead.id)}
                      onStageChange={(st) => moveStage(lead.id, st)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LeadCard({
  lead,
  onDragStart,
  onStageChange,
}: {
  lead: LeadRow;
  onDragStart: () => void;
  onStageChange: (s: LeadStage) => void;
}) {
  const [open, setOpen] = useState(false);
  const created = new Date(lead.created_at);
  return (
    <>
      <div
        draggable
        onDragStart={onDragStart}
        onClick={() => setOpen(true)}
        className="cursor-grab rounded-lg border border-border bg-card p-3 text-sm shadow-soft transition hover:shadow-card active:cursor-grabbing"
      >
        <div className="font-medium text-foreground">{lead.contact?.full_name ?? "Contacto"}</div>
        <div className="mt-1 flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground">
          <span>{lead.channel}</span>
          <span>·</span>
          <span>{lead.pipeline}</span>
        </div>
        {lead.property && (
          <div className="mt-2 flex items-start gap-1 rounded-md bg-secondary/60 px-2 py-1 text-xs text-muted-foreground">
            <HomeIcon className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{lead.property.title}</span>
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
          {lead.contact?.phone && (
            <a
              href={`https://wa.me/${lead.contact.phone.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </a>
          )}
          {lead.contact?.email && (
            <a
              href={`mailto:${lead.contact.email}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              <Mail className="h-3 w-3" /> Email
            </a>
          )}
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground">
          {created.toLocaleDateString("es-PY", { day: "2-digit", month: "short" })}
        </div>
      </div>

      {open && <LeadDrawer lead={lead} onClose={() => setOpen(false)} onStageChange={onStageChange} />}
    </>
  );
}

function LeadDrawer({
  lead,
  onClose,
  onStageChange,
}: {
  lead: LeadRow;
  onClose: () => void;
  onStageChange: (s: LeadStage) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-background p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">{lead.contact?.full_name}</h3>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {lead.channel} · {lead.pipeline}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        <div className="mt-5 space-y-3 text-sm">
          {lead.contact?.email && (
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {lead.contact.email}</div>
          )}
          {lead.contact?.phone && (
            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {lead.contact.phone}</div>
          )}
          {lead.property && (
            <div className="rounded-md border border-border bg-secondary/40 p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Propiedad de interés</div>
              <div className="mt-1 text-sm font-medium">{lead.property.title}</div>
              <a href={`/propiedad/${lead.property.slug}`} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary underline">
                Ver en el sitio
              </a>
            </div>
          )}
          {lead.message && (
            <div className="rounded-md bg-secondary/40 p-3 text-sm text-foreground/90 whitespace-pre-line">
              {lead.message}
            </div>
          )}
        </div>

        <div className="mt-6">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Cambiar etapa</label>
          <select
            aria-label="Etapa del lead"
            value={lead.stage}
            onChange={(e) => onStageChange(e.target.value as LeadStage)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {LEAD_STAGES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <QuickTaskForm leadId={lead.id} />
      </div>
    </div>
  );
}

function QuickTaskForm({ leadId }: { leadId: string }) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    const { error } = await supabase.from("tasks").insert({
      title: title.trim(),
      due_date: due || null,
      lead_id: leadId,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Tarea creada");
      setTitle("");
      setDue("");
      qc.invalidateQueries({ queryKey: ["tasks"] });
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-2 border-t border-border pt-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Nueva tarea para este lead
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Ej: Llamar mañana a las 10:00"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
      <div className="flex gap-2">
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          Crear
        </button>
      </div>
    </form>
  );
}
